import { unique } from "@foretagsplatsen/core-helpers/array";
import { widget } from "@foretagsplatsen/widgetjs";
import { withViewportScrollRetained } from "../../core/helpers/dom-util.js";
import HtmlEditorRenderer from "./visitors/editor/HtmlEditorRenderer.js";
import jQuery from "jquery";

/**
 * Report editor widget.
 *
 * The report editor is responsible for rendering the report (delegating the
 * actual rendering to a visitor) and managing the width of each column to
 * mimic a table layout.
 *
 * It also handles drag & drop of report rows and columns, updating the
 * report when an item is moved. Each time an item is moved, a
 * `templateChanged` event is triggered.
 *
 * When the edited report is changed from outside the editor, the setter
 * `setReport` should be called to update the editor's state and re-render
 * the report.
 *
 * @param {report} spec.report - Report to be edited
 */
export default widget.subclass((that, my) => {
	my.initialize = function ({ report }) {
		my.super(...arguments);
		my.report = report;
	};

	that.templateChanged = my.events.createEvent();

	my.set("report", (report) => {
		my.report = report;

		withViewportScrollRetained(() => that.update());
	});

	let resizing = false;

	that.resizeReport = function () {
		if (resizing) {
			return;
		}

		resizing = true;
		setTimeout(my.setupReportWidth, 0);

		setTimeout(() => {
			resizing = false;
			my.setupReportWidth();
		}, 100);
	};

	/**
	 * Optimization: instead of doing a full update, just change the
	 * .selected class.
	 */
	that.updateSelected = function (selected) {
		that.asJQuery().find(".selected").removeClass("selected");
		if (selected) {
			that.asJQuery()
				.find(`.selectable[reportid=${selected.getId()}]`)
				.addClass("selected");
		}
	};

	that.renderContentOn = function (html) {
		if (!my.report) {
			return;
		}

		my.report.withCoALanguage(() => my.renderReportOn(html));
	};

	my.renderReportOn = function (html) {
		const renderer = new HtmlEditorRenderer({
			report: my.report,
			onSectionToggled: () => that.update(),
		});

		html.div(
			{ klass: "reportEditor" },
			renderer.renderReportOn.bind(renderer),
		);
	};

	my.didAttach = function () {
		my.setupReportWidth();

		// Optimization: Do not block the rendering.
		//
		// jQuery can take up to 300ms to perform the .sortable and resizing
		// on large reports, so instead, we cache the previous value and set
		// that quicky to avoid visual glitches, then during the next tick
		// only we perform a proper update.
		setTimeout(() => {
			my.setupDraggable();
		}, 0);

		// Show tooltips in the report
		jQuery('[data-toggle="tooltip"]').tooltip();
	};

	my.setupReportWidth = function () {
		const elt = that.asJQuery();
		const report = elt.find(".report-editor");
		const firstColumn = elt.find(".title-column");
		const allColumns = elt.find(".report-column");
		const lastColumn = allColumns.last();

		// Hack: Fix FTGP-5974
		// Leave room for a possible scrollbar.
		//
		// The issue is that when starting dragging columns, a scrollbar
		// might appear. If so, there's less room for the columns, and the
		// last one might overflow and be displayed below the report, since
		// columns are displayed inline.
		const rightMargin = 20;

		// Remove any fixed width first
		firstColumn.width("");
		report.width("");

		let allColumnsWidth = 0;

		allColumns.each((index, col) => {
			allColumnsWidth += jQuery(col).width();
		});

		if (allColumns.length > 1) {
			// Handle overflowing issues with columns
			if (firstColumn.position().top === lastColumn.position().top) {
				// the title column should push other columns
				firstColumn.width(
					report.width() -
						rightMargin -
						(allColumnsWidth - firstColumn.width()),
				);
			} else {
				report.width(allColumnsWidth + 20); // Same white space as on the left side of the report
			}
		}

		jQuery(window).resize(that.resizeReport);
	};

	my.setupDraggable = function () {
		that.asJQuery()
			.find(".column-draggable")
			.sortable({
				axis: "x",
				update: my.updateColumns,
				helper: "clone",
				opacity: 0.7,
			})
			.disableSelection();

		that.asJQuery()
			.find(".row-draggable")
			.sortable({
				axis: "y",
				// For performance reasons with jQuery sortable, only take immediate
				// children of the list that are sortable (i.e., exclude not
				// sortable ones).
				items: "> :not(.no-sort)",
				connectWith: ".row-droppable",
				update: my.sortRows,
				// Prevent jQuery from hiding the original row.
				start: (evt, ui) => ui.item.show(),
				helper: "clone",
				opacity: 0.7,
			})
			.disableSelection();
	};

	/**
	 * Setup the columns to emulate a table.
	 *
	 * The report's first column should "push" the other columns to the
	 * right.
	 */
	my.updateColumns = function () {
		const template = my.report.getTemplate();
		const columns = that
			.asJQuery()
			.find(".column-draggable")
			.children()
			.get()
			.map((div) => template.getColumnById(jQuery(div).attr("reportid")));

		template.setColumns(columns);
		that.templateChanged.trigger();
	};

	/**
	 * Sort rows only when moved within the same parent.
	 *
	 * When moving a row from a parent to another, we want to sort only the
	 * destination.
	 */
	my.sortRows = function (event, ui) {
		if (event.target !== ui.item.parent().get(0)) {
			return;
		}
		my.updateRows(ui);
	};

	my.updateRows = function (ui) {
		my.updateGroup(ui.item.parent());
		that.templateChanged.trigger();
	};

	my.updateTemplate = function (report) {
		const template = my.report.getTemplate();
		const rows = report
			.children()
			.get()
			.map((div) => {
				const id = jQuery(div).attr("reportid");
				return template.getRowById(id);
			});

		template.setRows(rows);
	};

	my.updateGroup = function (sortableDiv) {
		const groupDiv = sortableDiv.parent();

		const reportTemplate = my.report.getTemplate();
		const newParent =
			reportTemplate.getRowById(groupDiv.attr("reportid")) ||
			reportTemplate;
		const rows = unique(
			sortableDiv
				.children()
				.get()
				.map((div) => jQuery(div).attr("reportid"))
				.map((id) => reportTemplate.getRowById(id)),
		);

		rows.forEach((row) => row.remove());
		newParent.setRows(rows);
	};
});
